import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, apiJson } from "../api/client";
import { socketClient } from "../api/socket";
import {
  wsLookupToPixelAndStatus,
  type WsPixelLookupContent,
} from "../api/wsPixelNormalize";
import { PhotoEditor, type PhotoEditorRef } from "../components/PhotoEditor";
import { useLocale } from "../contexts/LocaleContext";
import { formatMoney as formatMoneyInCurrency } from "../lib/format";
import type { AppCurrency } from "../lib/localePrefs";
import { useToast } from "../contexts/ToastContext";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { convertDecimalToDMS } from "../earth/pixel";
import type { Pixel } from "../types";

const DEFAULT_FRAME_IMG = "/static/img/gal12.jpg";

interface UserResponseSettings {
  artistType?: string;
  donationFirstName?: string;
  donationLastName?: string;
  donationPostalCode?: string;
  donationCity?: string;
  donationAddress?: string;
  donationCountry?: string;
  birthdate?: string;
  gender?: string;
}

interface UserResponse {
  name?: string;
  email?: string;
  created_at?: string;
  settings?: UserResponseSettings;
}

interface OwnedPixel extends Pixel {
  bought_at?: string;
  color?: string;
  paid_price?: number | null;
  paid_currency?: string | null;
  extra?: { customer?: { frameName?: string } };
}

function formatPaidPrice(
  price: number | null | undefined,
  currency: string | null | undefined,
  language: "de" | "en",
  dash: string,
): string {
  if (
    price == null ||
    !currency ||
    !["CHF", "EUR", "USD"].includes(currency)
  ) {
    return dash;
  }
  return formatMoneyInCurrency(price, language, currency as AppCurrency);
}

type SortKey = "date" | "rahmen" | "name";
type SortDir = "asc" | "desc";

export function ProfilePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t, formatDate, language } = useLocale();

  const detailRows = useMemo(
    (): Array<[string, (u: UserResponse) => string]> => [
      [t("profile.artistName"), (u) => u.name || t("common.dash")],
      [t("profile.artistType"), (u) => u.settings?.artistType || t("common.dash")],
      [t("profile.firstName"), (u) => u.settings?.donationFirstName || t("common.dash")],
      [t("profile.lastName"), (u) => u.settings?.donationLastName || t("common.dash")],
      [t("profile.email"), (u) => u.email || t("common.dash")],
      [
        t("profile.address"),
        (u) => {
          const s = u.settings;
          if (!s) return t("common.dash");
          const parts = [
            [s.donationPostalCode, s.donationCity].filter(Boolean).join(" "),
            s.donationAddress,
            s.donationCountry,
          ].filter(Boolean);
          return parts.length ? parts.join(", ") : t("common.dash");
        },
      ],
      [t("profile.birthdate"), (u) => formatDate(u.settings?.birthdate)],
      [t("profile.gender"), (u) => u.settings?.gender || t("common.dash")],
      [t("profile.memberSince"), (u) => formatDate(u.created_at)],
    ],
    [t, formatDate],
  );

  const [tab, setTab] = useState<"tab1" | "tab2">("tab1");
  const [user, setUser] = useState<UserResponse | null>(null);
  const [pixels, setPixels] = useState<OwnedPixel[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [popupPixel, setPopupPixel] = useState<OwnedPixel | null>(null);
  const [frameName, setFrameName] = useState("");
  const [frameColor, setFrameColor] = useState("#000000");
  const [editorSource, setEditorSource] = useState<File | string | null>(null);
  const [imageDirty, setImageDirty] = useState(false);
  const photoEditorRef = useRef<PhotoEditorRef>(null);

  const closeProfilePopup = useCallback(() => setPopupPixel(null), []);
  useEscapeKey(popupPixel !== null, closeProfilePopup);

  const reload = useCallback(async () => {
    try {
      const r = await apiFetch("/user");
      if (r.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      const data = (await r.json()) as UserResponse;
      setUser(data);
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
    try {
      const p = await apiJson<OwnedPixel[]>("/user/pixels");
      setPixels(Array.isArray(p) ? p : []);
    } catch (err) {
      console.error("Error fetching user pixels:", err);
    }
  }, [navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const sortedPixels = useMemo(() => {
    const arr = [...pixels];
    arr.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      if (sortKey === "date") {
        av = new Date(a.bought_at || 0).getTime();
        bv = new Date(b.bought_at || 0).getTime();
      } else if (sortKey === "rahmen") {
        av = a.pixel;
        bv = b.pixel;
      } else {
        av = (a.extra?.customer?.frameName || "").toLowerCase();
        bv = (b.extra?.customer?.frameName || "").toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [pixels, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  };

  const openPopup = async (p: OwnedPixel) => {
    try {
      await socketClient.connect();
      const raw =
        await socketClient.awaitLegacyResponse<WsPixelLookupContent>("pixel", {
          pixel: p.pixel,
        });
      const { pixel: wsPix } = wsLookupToPixelAndStatus(raw);
      const pix: OwnedPixel = {
        ...p,
        ...wsPix,
        bought_at: p.bought_at,
        extra: p.extra,
        color:
          typeof raw.color === "string" && raw.color ? raw.color : p.color,
        country:
          wsPix.country && wsPix.country.length > 0
            ? wsPix.country
            : p.country,
        location:
          wsPix.location && wsPix.location.length > 0
            ? wsPix.location
            : p.location,
      };
      setPopupPixel(pix);
      setFrameName(pix.frameName || "");
      setFrameColor(pix.color || "#000000");
      setEditorSource(pix.image ? `/gallery/${pix.image}` : DEFAULT_FRAME_IMG);
      setImageDirty(false);
    } catch {
      setPopupPixel(p);
      setFrameName(p.frameName || "");
      setFrameColor(p.color || "#000000");
      setEditorSource(p.image ? `/gallery/${p.image}` : DEFAULT_FRAME_IMG);
      setImageDirty(false);
    }
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.show(t("profile.selectImage"), "danger");
      return;
    }
    setEditorSource(file);
    setImageDirty(true);
    e.target.value = "";
  };

  const savePopup = async () => {
    if (!popupPixel) return;
    try {
      let imageData: string | undefined;
      if (imageDirty) {
        imageData =
          (await photoEditorRef.current?.exportCroppedJpeg()) ?? undefined;
      }
      await apiJson("/pixel", {
        method: "PUT",
        body: JSON.stringify({
          pixel: popupPixel.pixel,
          frameName: frameName.trim(),
          frameColor,
          ...(imageData ? { imageData } : {}),
        }),
      });
      toast.show(t("profile.saved"), "success");
      setPopupPixel(null);
      reload();
    } catch (err) {
      toast.show(
        t("profile.saveFailed", { error: (err as Error).message }),
        "danger",
      );
    }
  };

  return (
    <div className="content">
      <div className="profilbereich">
        <h1>{t("profile.title")}</h1>

        <div className="tabs">
          <button
            className={`tab-button ${tab === "tab1" ? "active" : ""}`}
            onClick={() => setTab("tab1")}
          >
            {t("profile.tabPersonal")}
          </button>
          <button
            className={`tab-button ${tab === "tab2" ? "active" : ""}`}
            onClick={() => setTab("tab2")}
          >
            {t("profile.tabActivity")}
          </button>
        </div>

        <div
          className="tab-content"
          id="tab1"
          style={{ display: tab === "tab1" ? "block" : "none" }}
        >
          <h2>{t("profile.personalData")}</h2>
          <table className="profil-tabelle">
            <tbody>
              {user &&
                detailRows.map(([label, getter]) => (
                  <tr key={label}>
                    <td>{label}:</td>
                    <td>{getter(user)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <p>
            <small>
              {t("profile.changeViaContact")}{" "}
              <Link to="/contact">{t("profile.contactPage")}</Link>.
            </small>
          </p>
        </div>

        <div
          className="tab-content"
          id="tab2"
          style={{ display: tab === "tab2" ? "block" : "none" }}
        >
          <h2>{t("profile.activity")}</h2>
          <table className="rahmen-tabelle">
            <thead>
              <tr>
                <th
                  className="sortable"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("date")}
                >
                  {t("profile.date")}{" "}
                  <SortIcon active={sortKey === "date"} dir={sortDir} />
                </th>
                <th
                  className="sortable"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("rahmen")}
                >
                  {t("profile.frame")}{" "}
                  <SortIcon active={sortKey === "rahmen"} dir={sortDir} />
                </th>
                <th
                  className="sortable"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("name")}
                >
                  {t("common.name")}{" "}
                  <SortIcon active={sortKey === "name"} dir={sortDir} />
                </th>
                <th>{t("profile.preview")}</th>
                <th>{t("common.price")}</th>
                <th>{t("profile.more")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedPixels.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    {t("profile.noFrames")}
                  </td>
                </tr>
              ) : (
                sortedPixels.map((p) => (
                  <tr key={p.pixel} data-pixel-id={p.pixel}>
                    <td>{formatDate(p.bought_at)}</td>
                    <td>{p.pixel}</td>
                    <td>{p.extra?.customer?.frameName || t("common.dash")}</td>
                    <td style={{ textAlign: "center" }}>
                      <img
                        src={
                          p.image
                            ? `/gallery/${p.image}`
                            : "/static/img/gal12.jpg"
                        }
                        alt={t("profile.framePreviewAlt")}
                        style={{
                          width: 100,
                          border: `3px solid ${p.color || "#000"}`,
                          borderRadius: 4,
                        }}
                      />
                    </td>
                    <td>
                      {formatPaidPrice(
                        p.paid_price,
                        p.paid_currency,
                        language,
                        t("common.dash"),
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => openPopup(p)}>
                        {t("profile.details")}
                      </button>
                      <button>{t("profile.downloadCert")}</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="rahmen-mobile-list">
            {sortedPixels.length === 0 ? (
              <div className="rahmen-mobile-empty">{t("profile.noFrames")}</div>
            ) : (
              sortedPixels.map((p) => (
                <article
                  className="rahmen-mobile-card"
                  key={`mobile-${p.pixel}`}
                  data-pixel-id={p.pixel}
                >
                  <div className="rahmen-mobile-field">
                    <span className="rahmen-mobile-label">{t("profile.date")}</span>
                    <span>{formatDate(p.bought_at)}</span>
                  </div>

                  <div className="rahmen-mobile-field">
                    <span className="rahmen-mobile-label">{t("profile.frame")}</span>
                    <span>{p.pixel}</span>
                  </div>

                  <div className="rahmen-mobile-field">
                    <span className="rahmen-mobile-label">{t("common.name")}</span>
                    <span>{p.extra?.customer?.frameName || t("common.dash")}</span>
                  </div>

                  <div className="rahmen-mobile-price-row">
                    <div className="rahmen-mobile-field">
                      <span className="rahmen-mobile-label">{t("common.price")}</span>
                      <span>
                        {formatPaidPrice(
                          p.paid_price,
                          p.paid_currency,
                          language,
                          t("common.dash"),
                        )}
                      </span>
                    </div>

                    <button className="rahmen-mobile-cert-button">
                      {t("profile.downloadCert")}
                    </button>
                  </div>

                  <div className="rahmen-mobile-preview">
                    <img
                      src={
                        p.image
                          ? `/gallery/${p.image}`
                          : "/static/img/gal12.jpg"
                      }
                      alt={t("profile.framePreviewAlt")}
                      style={{ borderColor: p.color || "#000" }}
                    />
                    <button
                      className="rahmen-mobile-details-button"
                      onClick={() => openPopup(p)}
                    >
                      {t("profile.details")}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </div>

      {popupPixel && (
        <div
          className="popup-overlay is-open"
          id="popup"
          aria-hidden="false"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPopupPixel(null);
          }}
        >
          <div
            className="popup-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-title"
          >
            <button
              className="close-btn"
              type="button"
              aria-label={t("profile.closePopup")}
              onClick={() => setPopupPixel(null)}
            >
              ×
            </button>

            <div className="image-wrap image-wrap--editable">
              <PhotoEditor
                ref={photoEditorRef}
                source={editorSource}
                onEdit={() => setImageDirty(true)}
              />
              <label className="upload-overlay" htmlFor="file-input">
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
                <span className="upload-text">{t("profile.uploadImage")}</span>
              </label>
            </div>

            <div className="frame-settings">
              <label className="field">
                <span>
                  <strong>{t("profile.frameName")} </strong>
                </span>
                <input
                  type="text"
                  placeholder={t("profile.frameNamePlaceholder")}
                  value={frameName}
                  onChange={(e) => setFrameName(e.target.value)}
                />
              </label>

              <label className="field color-field">
                <span>
                  <strong>{t("profile.frameColor")}</strong>
                </span>
                <input
                  type="color"
                  value={frameColor}
                  onChange={(e) => setFrameColor(e.target.value)}
                />
                <span className="color-hex">{frameColor.toUpperCase()}</span>
              </label>
            </div>

            <div className="popup-info">
              <p id="popup-title" className="visually-hidden">
                {t("profile.artworkInfo")}
              </p>
              <p>
                <strong>{t("common.artist")}:</strong>{" "}
                <span>{popupPixel.artistName || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("common.location")}:</strong>{" "}
                <span>{popupPixel.location || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("common.country")}:</strong>{" "}
                <span>{popupPixel.country || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("profile.pixel")}</strong>{" "}
                <span>{popupPixel.pixel}</span>
              </p>
              <p>
                <strong>{t("common.coordinates")}:</strong>{" "}
                <span className="coordinates">
                  {popupPixel.info
                    ? `${convertDecimalToDMS(
                        popupPixel.info.lat,
                        "lat",
                      )}, ${convertDecimalToDMS(popupPixel.info.lng, "lng")}`
                    : t("common.dash")}
                </span>
              </p>

              <div className="frame-meta">
                <p>
                  <strong>{t("common.frame")}:</strong>{" "}
                  <span>{frameName || t("common.dash")}</span>
                </p>
                <p>
                  <strong>{t("common.color")}:</strong>{" "}
                  <span
                    className="frame-color-dot"
                    style={{ background: frameColor }}
                  />
                </p>
              </div>

              <button className="btn-save" onClick={savePopup}>
                {t("profile.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <i className="fas fa-sort" />;
  return <i className={`fas fa-sort-${dir === "asc" ? "up" : "down"}`} />;
}
